import type {
  LandGateAffectedSection,
  LandGateConstraintType,
  LandGateInfrastructureType,
  LandGatePromotedFact,
  LandGateReviewEvent,
  SiteLayer,
  SiteLayerEvidenceMeta,
} from '../../types/site';
import type { EvidenceSection, SiteEvidenceDocument } from '../../types/siteEvidence';
import { hashCanonicalJson } from '../reportGeneration/reportContractHash';

const MAX_FACTS = 8;
const MAX_HISTORY = 50;
const MAX_TEXT = 160;
const CONSTRAINT_TYPES = new Set<LandGateConstraintType>([
  'floodplain', 'wetland', 'protected-area', 'setback', 'slope', 'other',
]);
const INFRASTRUCTURE_TYPES = new Set<LandGateInfrastructureType>([
  'electric', 'pipeline', 'fiber', 'transport', 'water', 'other',
]);

export const LANDGATE_AFFECTED_SECTIONS = [
  'parcel',
  'regulatory',
  'environmental',
  'onSiteInfrastructure',
] as const satisfies readonly LandGateAffectedSection[];

export interface ReviewedLandGateReconciliation {
  /** Sections whose persisted SED projection changed. */
  changedSections: LandGateAffectedSection[];
  /** Existing target sections that consumed a fully-valid review snapshot. */
  acknowledgedSections: LandGateAffectedSection[];
  /** SHA-256 of the exact canonical LandGate review state consumed. */
  inputSignature: string;
  /** Invalid client-authored review records are excluded and never acknowledged. */
  validationIssues: Array<{ layerId: string; reason: string }>;
}

export interface LandGateDirtyAcknowledgementPlan {
  signatureMatched: boolean;
  clearedSections: LandGateAffectedSection[];
  remainingSections: string[];
}

export interface LandGateReviewActor {
  uid: string;
  email?: string | null;
}

function isLandGateAffectedSection(value: string): value is LandGateAffectedSection {
  return (LANDGATE_AFFECTED_SECTIONS as readonly string[]).includes(value);
}

const LEGACY_NON_EVIDENCE_DIRTY_LABELS = new Set(['adamsDocuments', 'siteResearch']);

export interface LandGatePromotionInput {
  facts: LandGatePromotedFact[];
}

export interface LandGateReviewTransition {
  layerId: string;
  layerName: string;
  action: 'promote' | 'unpromote';
  affectedSections: LandGateAffectedSection[];
  facts: LandGatePromotedFact[];
  sourceFeature: LandGateReviewEvent['sourceFeature'];
  snapshot: LandGateReviewEvent['snapshot'];
}

function sanitizeText(value: unknown, field: string, required = false): string | undefined {
  if (value == null) {
    if (required) throw new Error(`${field} is required`);
    return undefined;
  }
  const text = String(value)
    .replace(/<[^>]*>/g, ' ')
    .replace(/[\u0000-\u001f\u007f]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, MAX_TEXT);
  if (!text) {
    if (required) throw new Error(`${field} is required`);
    return undefined;
  }
  return text;
}

function sanitizeAcreage(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0 || n > 10_000_000) {
    throw new Error('Parcel acreage must be greater than zero and no more than 10,000,000 acres');
  }
  return Number(n.toFixed(4));
}

function sanitizeUrl(value: unknown): string | undefined {
  const text = sanitizeText(value, 'Source URL');
  if (!text) return undefined;
  try {
    const parsed = new URL(text);
    return parsed.protocol === 'https:' ? parsed.toString().slice(0, 500) : undefined;
  } catch {
    return undefined;
  }
}

function sanitizeEvidenceHash(value: unknown): string | undefined {
  if (value == null) return undefined;
  const hash = sanitizeText(value, 'Source evidence hash', true)!;
  if (!/^[a-f0-9]{64}$/.test(hash)) {
    throw new Error('Source evidence hash must be a SHA-256 hex digest');
  }
  return hash;
}

export function sanitizeLandGateFact(fact: LandGatePromotedFact): LandGatePromotedFact {
  switch (fact.kind) {
    case 'parcel': {
      const parcelId = sanitizeText(fact.parcelId, 'Parcel ID');
      const owner = sanitizeText(fact.owner, 'Owner');
      const acreage = sanitizeAcreage(fact.acreage);
      if (!parcelId && !owner && acreage == null) {
        throw new Error('A parcel promotion requires parcel ID, acreage, or owner');
      }
      return {
        kind: 'parcel',
        ...(parcelId ? { parcelId } : {}),
        ...(acreage != null ? { acreage } : {}),
        ...(owner ? { owner } : {}),
      };
    }
    case 'zoning': {
      const zoningCode = sanitizeText(fact.zoningCode, 'Zoning code', true)!;
      const jurisdiction = sanitizeText(fact.jurisdiction, 'Jurisdiction');
      return { kind: 'zoning', zoningCode, ...(jurisdiction ? { jurisdiction } : {}) };
    }
    case 'constraint': {
      if (!CONSTRAINT_TYPES.has(fact.constraintType)) throw new Error('Unsupported constraint type');
      const detail = sanitizeText(fact.detail, 'Constraint detail');
      return {
        kind: 'constraint',
        constraintType: fact.constraintType,
        present: fact.present === true,
        ...(detail ? { detail } : {}),
      };
    }
    case 'existing-infrastructure': {
      if (!INFRASTRUCTURE_TYPES.has(fact.infrastructureType)) {
        throw new Error('Unsupported infrastructure type');
      }
      return {
        kind: 'existing-infrastructure',
        infrastructureType: fact.infrastructureType,
        label: sanitizeText(fact.label, 'Infrastructure label', true)!,
      };
    }
    default: {
      const exhaustive: never = fact;
      throw new Error(`Unsupported LandGate fact: ${String(exhaustive)}`);
    }
  }
}

export function affectedSectionsForLandGateFacts(
  facts: readonly LandGatePromotedFact[],
): LandGateAffectedSection[] {
  const sections = new Set<LandGateAffectedSection>();
  for (const fact of facts) {
    if (fact.kind === 'parcel') sections.add('parcel');
    if (fact.kind === 'zoning') {
      sections.add('parcel');
      sections.add('regulatory');
    }
    if (fact.kind === 'constraint') sections.add('environmental');
    if (fact.kind === 'existing-infrastructure') sections.add('onSiteInfrastructure');
  }
  return [...sections].sort();
}

export function defaultEvidenceForLayer(
  layer: Pick<SiteLayer, 'source' | 'importedAt'>,
): SiteLayerEvidenceMeta {
  const use = layer.source === 'landgate-import'
    || layer.source === 'doc-derived'
    || layer.source === 'scenario-derived'
    ? 'map-only'
    : layer.source === 'arcgis-import' || layer.source === 'peeringdb-import'
      ? 'screening'
      : 'site-truth';
  return {
    use,
    source: {
      providerId: layer.source === 'landgate-import' ? 'landgate' : layer.source,
      retrievedAt: layer.importedAt,
    },
  };
}

function normalizedActor(actor: LandGateReviewActor): { uid: string; email?: string } {
  const uid = sanitizeText(actor.uid, 'Reviewer', true)!;
  const email = sanitizeText(actor.email, 'Reviewer email');
  return { uid, ...(email ? { email } : {}) };
}

function reviewSourceFeature(layer: SiteLayer): LandGateReviewEvent['sourceFeature'] {
  const source = layer.evidence?.source ?? defaultEvidenceForLayer(layer).source;
  const featureId = sanitizeText(source?.featureId, 'Source feature ID');
  const sourceUrl = sanitizeUrl(source?.sourceUrl);
  const retrievedAt = sanitizeText(source?.retrievedAt, 'Source retrieval date') ?? layer.importedAt;
  const dataAsOf = sanitizeText(source?.dataAsOf, 'Source data vintage');
  const evidenceHash = sanitizeEvidenceHash(source?.evidenceHash);
  return {
    providerId: 'landgate',
    retrievedAt,
    ...(featureId ? { featureId } : {}),
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(dataAsOf ? { dataAsOf } : {}),
    ...(evidenceHash ? { evidenceHash } : {}),
  };
}

function reviewSnapshot(
  layer: SiteLayer,
  facts: LandGatePromotedFact[],
): LandGateReviewEvent['snapshot'] {
  const layerName = sanitizeText(layer.name, 'Layer name', true)!;
  const contentHash = sanitizeText(layer.contentHash, 'Layer content hash');
  return {
    layerName,
    category: layer.category,
    geometryType: layer.geometry.type,
    ...(contentHash ? { contentHash } : {}),
    facts,
  };
}

export function promoteLandGateLayer(
  layer: SiteLayer,
  input: LandGatePromotionInput,
  actor: LandGateReviewActor,
  at = new Date().toISOString(),
): SiteLayer {
  if (layer.source !== 'landgate-import') throw new Error('Only LandGate layers can be promoted');
  if (!Array.isArray(input.facts) || input.facts.length === 0 || input.facts.length > MAX_FACTS) {
    throw new Error(`Select between 1 and ${MAX_FACTS} report facts`);
  }
  const facts = input.facts.map(sanitizeLandGateFact);
  const affectedSections = affectedSectionsForLandGateFacts(facts);
  const current = layer.evidence?.promotion;
  const reviewer = normalizedActor(actor);
  const event = {
    id: `landgate-review-${at}-${reviewer.uid}`,
    action: 'promote' as const,
    at,
    actor: reviewer,
    sourceFeature: reviewSourceFeature(layer),
    snapshot: reviewSnapshot(layer, facts),
    facts,
    affectedSections,
  };
  return {
    ...layer,
    evidence: {
      ...(layer.evidence ?? defaultEvidenceForLayer(layer)),
      use: 'screening',
      promotion: {
        state: 'promoted',
        promotedAt: at,
        promotedBy: reviewer,
        facts,
        history: [...(current?.history ?? []), event].slice(-MAX_HISTORY),
      },
    },
  };
}

export function unpromoteLandGateLayer(
  layer: SiteLayer,
  actor: LandGateReviewActor,
  at = new Date().toISOString(),
): SiteLayer {
  if (layer.source !== 'landgate-import') throw new Error('Only LandGate layers can be unpromoted');
  const current = layer.evidence?.promotion;
  if (current?.state !== 'promoted') return layer;
  const affectedSections = affectedSectionsForLandGateFacts(current.facts);
  const event = {
    id: `landgate-review-${at}-${normalizedActor(actor).uid}`,
    action: 'unpromote' as const,
    at,
    actor: normalizedActor(actor),
    sourceFeature: reviewSourceFeature(layer),
    snapshot: reviewSnapshot(layer, current.facts),
    facts: current.facts,
    affectedSections,
  };
  return {
    ...layer,
    evidence: {
      ...(layer.evidence ?? defaultEvidenceForLayer(layer)),
      use: 'map-only',
      promotion: {
        state: 'map-only',
        facts: [],
        history: [...current.history, event].slice(-MAX_HISTORY),
      },
    },
  };
}

function reviewSignature(layer: SiteLayer | undefined): string {
  const review = layer?.evidence?.promotion;
  return JSON.stringify({
    visible: layer?.visible !== false,
    state: review?.state ?? 'map-only',
    facts: review?.facts ?? [],
    history: review?.history ?? [],
  });
}

function landGateReviewIdentity(layer: SiteLayer): string {
  const featureId = layer.evidence?.source?.featureId;
  const sourceEvidenceHash = layer.evidence?.source?.evidenceHash;
  if (featureId) {
    return `feature:${featureId}|geometry:${layer.geometry.type}|hash:${sourceEvidenceHash ?? layer.contentHash ?? ''}`;
  }
  if (sourceEvidenceHash) return `source-hash:${sourceEvidenceHash}`;
  if (layer.contentHash) return `hash:${layer.contentHash}`;
  return `id:${layer.id}`;
}

export function landGateReviewTransitions(
  before: readonly SiteLayer[] | undefined,
  after: readonly SiteLayer[] | undefined,
): LandGateReviewTransition[] {
  const beforeByIdentity = new Map((before ?? []).map(layer => [landGateReviewIdentity(layer), layer]));
  return (after ?? []).flatMap(layer => {
    if (layer.source !== 'landgate-import') return [];
    const prior = beforeByIdentity.get(landGateReviewIdentity(layer));
    if (reviewSignature(prior) === reviewSignature(layer)) return [];
    const review = layer.evidence?.promotion;
    const lastEvent = review?.history.at(-1);
    if (!lastEvent) return [];
    return [{
      layerId: layer.id,
      layerName: layer.name,
      action: lastEvent.action,
      affectedSections: lastEvent.affectedSections,
      facts: lastEvent.facts,
      sourceFeature: lastEvent.sourceFeature,
      snapshot: lastEvent.snapshot,
    }];
  });
}

export function dirtySectionsForLandGateReviewChanges(
  before: readonly SiteLayer[] | undefined,
  after: readonly SiteLayer[] | undefined,
): LandGateAffectedSection[] {
  const sections = new Set<LandGateAffectedSection>();
  for (const transition of landGateReviewTransitions(before, after)) {
    transition.affectedSections.forEach(section => sections.add(section));
  }
  const afterIdentities = new Set((after ?? []).map(landGateReviewIdentity));
  for (const prior of before ?? []) {
    if (prior.source !== 'landgate-import' || afterIdentities.has(landGateReviewIdentity(prior))) continue;
    const review = prior.evidence?.promotion;
    if (review?.state !== 'promoted') continue;
    affectedSectionsForLandGateFacts(review.facts).forEach(section => sections.add(section));
  }
  return [...sections].sort();
}

/** Preserve the reviewed state when a fresh vendor import wins dedup. */
export function preserveLandGateReview(winner: SiteLayer, candidates: readonly SiteLayer[]): SiteLayer {
  if (winner.source !== 'landgate-import') return winner;
  const reviewed = candidates
    .filter(candidate => candidate.source === 'landgate-import' && candidate.evidence?.promotion)
    .sort((a, b) => {
      const aAt = a.evidence?.promotion?.history.at(-1)?.at ?? '';
      const bAt = b.evidence?.promotion?.history.at(-1)?.at ?? '';
      return bAt.localeCompare(aAt);
    })[0];
  if (!reviewed?.evidence?.promotion) return winner;
  if (reviewed === winner) return winner;
  const winnerEvidenceHash = winner.evidence?.source.evidenceHash;
  const reviewedEvent = reviewed.evidence.promotion.history.at(-1);
  const reviewedEvidenceHash = reviewedEvent?.action === 'promote'
    ? reviewedEvent.sourceFeature.evidenceHash
    : reviewed.evidence.source.evidenceHash;
  // A review may move to a duplicate winner only when both rows prove they
  // represent the same provider-owned evidence. Legacy/mismatched identities
  // fail closed and require a fresh review instead of blessing new geometry.
  if (
    !winnerEvidenceHash
    || !reviewedEvidenceHash
    || winnerEvidenceHash !== reviewedEvidenceHash
  ) {
    return winner;
  }
  return {
    ...winner,
    evidence: {
      ...(winner.evidence ?? defaultEvidenceForLayer(winner)),
      use: reviewed.evidence.use,
      promotion: reviewed.evidence.promotion,
    },
  };
}

/**
 * Hash only the LandGate state that can affect reviewed SED projection. The
 * full promotion history is included: an unpromotion or deletion therefore
 * changes the signature even when a freshly-compiled SED has no stale row to
 * remove. Raw vendor attributes and geometry coordinates are intentionally
 * excluded because they are map-only and never report evidence.
 */
export function reviewedLandGateEvidenceInputSignature(
  layers: readonly SiteLayer[] | undefined,
): string {
  const rows = (layers ?? [])
    .filter(layer => layer.source === 'landgate-import')
    .map(layer => ({
      id: layer.id,
      visible: layer.visible !== false,
      source: layer.evidence?.source ?? null,
      promotion: layer.evidence?.promotion ?? null,
    }))
    .sort((a, b) => a.id.localeCompare(b.id));
  return hashCanonicalJson({ schemaVersion: 1, layers: rows });
}

function isEvidenceSection(
  value: unknown,
): value is EvidenceSection<unknown> {
  return Boolean(value && typeof value === 'object' && 'status' in value);
}

function parseReviewTimestamp(value: unknown, field: string): string {
  const text = sanitizeText(value, field, true)!;
  if (!Number.isFinite(Date.parse(text))) throw new Error(`${field} must be a valid timestamp`);
  return text;
}

function validatedPromotedReview(layer: SiteLayer): {
  reviewEventId: string;
  reviewedAt: string;
  facts: LandGatePromotedFact[];
  actorUid: string;
  featureId?: string;
  retrievedAt: string;
  dataAsOf?: string;
  sourceUrl?: string;
  evidenceHash?: string;
} {
  const review = layer.evidence?.promotion;
  if (review?.state !== 'promoted') throw new Error('Layer is not promoted');
  if (!Array.isArray(review.facts) || review.facts.length === 0 || review.facts.length > MAX_FACTS) {
    throw new Error(`Promotion must contain between 1 and ${MAX_FACTS} facts`);
  }
  if (!Array.isArray(review.history)) throw new Error('Promotion history must be an array');
  if (layer.evidence?.source?.providerId !== 'landgate') {
    throw new Error('Promotion source must be LandGate');
  }
  const facts = review.facts.map(sanitizeLandGateFact);
  const event = review.history.at(-1);
  if (!event || event.action !== 'promote') {
    throw new Error('Promoted state must end with a promote event');
  }
  if (!event.actor || typeof event.actor !== 'object') throw new Error('Review event actor is malformed');
  if (!event.sourceFeature || typeof event.sourceFeature !== 'object') {
    throw new Error('Review event source is malformed');
  }
  if (!event.snapshot || typeof event.snapshot !== 'object') {
    throw new Error('Review event snapshot is malformed');
  }
  if (!Array.isArray(event.facts)) throw new Error('Review event facts must be an array');
  if (!Array.isArray(event.snapshot.facts)) throw new Error('Review snapshot facts must be an array');
  if (!Array.isArray(event.affectedSections)) {
    throw new Error('Review event affected sections must be an array');
  }
  const reviewEventId = sanitizeText(event.id, 'Review event ID', true)!;
  const reviewedAt = parseReviewTimestamp(event.at, 'Reviewed at');
  const actor = normalizedActor(event.actor);
  if (review.promotedBy && normalizedActor(review.promotedBy).uid !== actor.uid) {
    throw new Error('Promotion reviewer does not match the terminal review event');
  }
  if (event.sourceFeature.providerId !== 'landgate') {
    throw new Error('Review event source must be LandGate');
  }
  const retrievedAt = parseReviewTimestamp(event.sourceFeature.retrievedAt, 'Source retrieval date');
  const sourceUrl = sanitizeUrl(event.sourceFeature.sourceUrl);
  if (event.sourceFeature.sourceUrl && !sourceUrl) {
    throw new Error('Review event source URL must be HTTPS');
  }
  const currentFeatureId = sanitizeText(layer.evidence.source.featureId, 'Source feature ID');
  const eventFeatureId = sanitizeText(event.sourceFeature.featureId, 'Review feature ID');
  if (currentFeatureId && eventFeatureId && currentFeatureId !== eventFeatureId) {
    throw new Error('Review event feature does not match the canonical layer');
  }
  const currentEvidenceHash = sanitizeEvidenceHash(layer.evidence.source.evidenceHash);
  const eventEvidenceHash = sanitizeEvidenceHash(event.sourceFeature.evidenceHash);
  if (
    (currentEvidenceHash || eventEvidenceHash)
    && currentEvidenceHash !== eventEvidenceHash
  ) {
    throw new Error('Review event evidence does not match the canonical layer');
  }
  const eventFacts = event.facts.map(sanitizeLandGateFact);
  const snapshotFacts = event.snapshot.facts.map(sanitizeLandGateFact);
  if (
    hashCanonicalJson(eventFacts) !== hashCanonicalJson(facts)
    || hashCanonicalJson(snapshotFacts) !== hashCanonicalJson(facts)
  ) {
    throw new Error('Review event facts do not match the promoted facts');
  }
  const affectedSections = affectedSectionsForLandGateFacts(facts);
  if (hashCanonicalJson([...event.affectedSections].sort()) !== hashCanonicalJson(affectedSections)) {
    throw new Error('Review event affected sections do not match its facts');
  }
  const dataAsOf = sanitizeText(event.sourceFeature.dataAsOf, 'Source data vintage');
  return {
    reviewEventId,
    reviewedAt,
    facts,
    actorUid: actor.uid,
    ...(eventFeatureId ? { featureId: eventFeatureId } : {}),
    retrievedAt,
    ...(dataAsOf ? { dataAsOf } : {}),
    ...(sourceUrl ? { sourceUrl } : {}),
    ...(eventEvidenceHash ? { evidenceHash: eventEvidenceHash } : {}),
  };
}

export interface ValidatedReviewedLandGateLayer {
  layer: SiteLayer;
  review: ReturnType<typeof validatedPromotedReview>;
}

/** Fail-closed validation shared by SED projection and direct prompt context. */
export function validatedReviewedLandGateLayers(
  layers: readonly SiteLayer[] | undefined,
): {
  reviews: ValidatedReviewedLandGateLayer[];
  validationIssues: Array<{ layerId: string; reason: string }>;
} {
  const reviews: ValidatedReviewedLandGateLayer[] = [];
  const validationIssues: Array<{ layerId: string; reason: string }> = [];
  for (const layer of layers ?? []) {
    if (layer.source !== 'landgate-import' || layer.visible === false) continue;
    if (layer.evidence?.promotion?.state !== 'promoted') continue;
    try {
      reviews.push({ layer, review: validatedPromotedReview(layer) });
    } catch (error) {
      validationIssues.push({
        layerId: layer.id,
        reason: error instanceof Error ? error.message : 'Invalid LandGate review record',
      });
    }
  }
  return { reviews, validationIssues };
}

/**
 * Rebuild reviewed facts beside canonical section data. Provider/document
 * fields are never overwritten. Validation is deliberately fail-closed:
 * malformed client-authored review records are omitted and prevent dirty-state
 * acknowledgement. Reviewer identity remains explicitly client-recorded and
 * non-citable until promotion writes move behind an authenticated server API.
 */
export function reconcileReviewedLandGateEvidence(
  sed: SiteEvidenceDocument,
  layers: readonly SiteLayer[] | undefined,
): ReviewedLandGateReconciliation {
  const sectionSignature = (sectionId: LandGateAffectedSection): string => {
    const section = sed[sectionId as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined;
    return hashCanonicalJson(section?.reviewedScreeningEvidence ?? []);
  };
  const before = new Map(
    LANDGATE_AFFECTED_SECTIONS.map(sectionId => [sectionId, sectionSignature(sectionId)]),
  );
  const validatedInput = validatedReviewedLandGateLayers(layers);
  const validationIssues = validatedInput.validationIssues;

  // Rebuild the LandGate contribution from the current layer set. This also
  // removes evidence from deleted, hidden, or unpromoted layers without
  // touching any future non-LandGate screening evidence.
  for (const sectionId of LANDGATE_AFFECTED_SECTIONS) {
    const section = sed[sectionId as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined;
    if (!isEvidenceSection(section) || !section.reviewedScreeningEvidence) continue;
    section.reviewedScreeningEvidence = section.reviewedScreeningEvidence
      .filter(item => item.providerId !== 'landgate');
  }

  for (const { layer, review: validated } of validatedInput.reviews) {
    const {
      reviewEventId,
      reviewedAt,
      facts,
      actorUid,
      featureId,
      retrievedAt,
      dataAsOf,
      sourceUrl,
      evidenceHash,
    } = validated;
    const bySection = new Map<LandGateAffectedSection, LandGatePromotedFact[]>();
    for (const fact of facts) {
      for (const sectionId of affectedSectionsForLandGateFacts([fact])) {
        bySection.set(sectionId, [...(bySection.get(sectionId) ?? []), fact]);
      }
    }
    for (const [sectionId, sectionFacts] of bySection) {
      const section = sed[sectionId as keyof SiteEvidenceDocument] as EvidenceSection<unknown> | undefined;
      if (!isEvidenceSection(section)) continue;
      const reviewContentHash = hashCanonicalJson({
        schemaVersion: 1,
        providerId: 'landgate',
        layerId: layer.id,
        reviewEventId,
        reviewedAt,
        sourceFeature: {
          providerId: 'landgate',
          featureId: featureId ?? null,
          retrievedAt,
          dataAsOf: dataAsOf ?? null,
          evidenceHash: evidenceHash ?? null,
        },
        facts: sectionFacts,
      });
      section.reviewedScreeningEvidence = [
        ...(section.reviewedScreeningEvidence ?? []).filter(item => item.layerId !== layer.id),
        {
          providerId: 'landgate',
          sourceGrade: 'reviewed-landgate',
          qualification: 'screening',
          citationEligible: false,
          provenanceAuthority: 'client-recorded-review',
          reviewEventId,
          reviewContentHash,
          layerId: layer.id,
          ...(featureId
            ? { featureId }
            : {}),
          ...(sourceUrl ? { sourceUrl } : {}),
          retrievedAt,
          ...(dataAsOf
            ? { dataAsOf }
            : {}),
          reviewedAt,
          // UID is retained only as client-recorded audit context. Email is
          // deliberately stripped because the server cannot authenticate a
          // client-authored historic event actor.
          reviewedBy: { uid: actorUid },
          facts: sectionFacts,
        },
      ];
    }
  }

  const changedSections = LANDGATE_AFFECTED_SECTIONS.filter(
    sectionId => before.get(sectionId) !== sectionSignature(sectionId),
  );
  const acknowledgedSections = validationIssues.length === 0
    ? LANDGATE_AFFECTED_SECTIONS.filter(sectionId =>
        isEvidenceSection(sed[sectionId as keyof SiteEvidenceDocument]),
      )
    : [];
  return {
    changedSections,
    acknowledgedSections,
    inputSignature: reviewedLandGateEvidenceInputSignature(layers),
    validationIssues,
  };
}

/**
 * Clear dirty sections only when the live canonical review state is byte-for-
 * byte equivalent to the state reconciled into the SED. The pure plan is used
 * by both Admin and web Firestore transactions.
 */
export function planLandGateDirtyAcknowledgement(params: {
  liveLayers: readonly SiteLayer[] | undefined;
  consumedInputSignature: string;
  dirtySections: readonly string[] | undefined;
  acknowledgedSections: readonly LandGateAffectedSection[];
}): LandGateDirtyAcknowledgementPlan {
  // Early site records used provider/workflow labels (for example
  // `adamsDocuments`) in this field. They have no mapping to a SED evidence
  // section and therefore can never be acknowledged. Drop only the two known
  // obsolete labels as part of the same successful pointer commit that
  // publishes a rebuilt SED. Any other unknown/future label remains fail-closed
  // instead of being silently discarded.
  const remaining = [...new Set(
    (params.dirtySections ?? []).filter(
      sectionId => !LEGACY_NON_EVIDENCE_DIRTY_LABELS.has(sectionId),
    ),
  )].sort();
  const signatureMatched = reviewedLandGateEvidenceInputSignature(params.liveLayers)
    === params.consumedInputSignature;
  if (!signatureMatched) {
    return { signatureMatched, clearedSections: [], remainingSections: remaining };
  }
  const acknowledged = new Set(params.acknowledgedSections);
  const clearedSections = remaining.filter(
    (sectionId): sectionId is LandGateAffectedSection => (
      isLandGateAffectedSection(sectionId) && acknowledged.has(sectionId)
    ),
  );
  return {
    signatureMatched,
    clearedSections,
    remainingSections: remaining.filter(
      sectionId => !isLandGateAffectedSection(sectionId) || !acknowledged.has(sectionId),
    ),
  };
}

/** Back-compatible convenience wrapper for callers interested only in output diffs. */
export function applyReviewedLandGateEvidence(
  sed: SiteEvidenceDocument,
  layers: readonly SiteLayer[] | undefined,
): LandGateAffectedSection[] {
  return reconcileReviewedLandGateEvidence(sed, layers).changedSections;
}
